// ---------------CAROUSAL CARD COMPONENT-----------------------
import React, { useMemo, useEffect, useRef } from "react";
import { Row, Form, Button, Alert } from "react-bootstrap";
import RcsDropMediaAndPreview from "./RcsDropMediaAndPreview";
import RcsSuggestionsField from "./RcsSuggestionsField";
import { FcPlus } from "react-icons/fc";

const getAllCarouselVariableNumbers = (cards, field, prefix) => {
  const regex = new RegExp(`\\[${prefix}(\\d+)\\]`, "g");
  const numbers = new Set();
  cards.forEach((card) => {
    let match;
    const text = card[field] || "";
    while ((match = regex.exec(text))) {
      numbers.add(Number(match[1]));
    }
  });
  return numbers;
};

const getNextCarouselVariableName = (cards, field, prefix) => {
  const numbers = getAllCarouselVariableNumbers(cards, field, prefix);
  let next = 1;
  while (numbers.has(next)) {
    next++;
  }
  return `[${prefix}${next}]`;
};

const insertAtCursor = (ref, value, setValue, insertText) => {
  if (!ref.current) {
    setValue((prev) => prev + insertText);
    return;
  }
  const input = ref.current;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const newValue = value.slice(0, start) + insertText + value.slice(end);
  setValue(newValue);
  setTimeout(() => {
    input.focus();
    input.setSelectionRange(
      start + insertText.length,
      start + insertText.length
    );
  }, 0);
};

const CarouselCardAddVariableButton = ({
  value,
  setCard,
  inputRef,
  prefix,
  carouselCards,
  cardIndex,
  field,
}) => {
  return (
    <Button
      onClick={() => {
        const nextVar = getNextCarouselVariableName(
          carouselCards,
          field,
          prefix
        );
        insertAtCursor(
          inputRef,
          value,
          (newValue) =>
            setCard((card) => ({
              ...card,
              [field]: newValue,
            })),
          nextVar
        );
      }}
      bsPrefix="text"
      style={{
        border: "none",
        background: "none",
        padding: 0,
        color: "inherit",
      }}
      variant="secondary"
      className="d-flex align-items-center text-success fw-bold"
      type="button"
    >
      <FcPlus className="icon icon-xs me-2" />
      Add Variable
    </Button>
  );
};

const RcsCarouselCardForm = ({
  card,
  setCard,
  idPrefix,
  suggestionInput,
  setSuggestionInput,
  addSuggestion,
  editingSuggestionIndex,
  setEditingSuggestionIndex,
  onEditSuggestion,
  onDeleteSuggestion,
  file,
  onFileChange,
  mediaError,
  thumbnailFile,
  onThumbnailFileChange,
  thumbnailError,
  carouselCards,
  cardIndex,
  carouselCardHeight,
  carouselCardWidth,
  mediaAccept,
  thumbnailAccept,
}) => {
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  const preview = useMemo(
    () => (file && !mediaError ? URL.createObjectURL(file) : null),
    [file, mediaError]
  );
  const thumbnailPreview = useMemo(
    () =>
      thumbnailFile && !thumbnailError
        ? URL.createObjectURL(thumbnailFile)
        : null,
    [thumbnailFile, thumbnailError]
  );

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
      if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    };
  }, [preview, thumbnailPreview]);

  const TitleAddVariableButton = (props) => (
    <CarouselCardAddVariableButton
      {...props}
      setCard={setCard}
      prefix="custom_param_title_"
      carouselCards={carouselCards}
      cardIndex={cardIndex}
      field="title"
    />
  );
  const DescriptionAddVariableButton = (props) => (
    <CarouselCardAddVariableButton
      {...props}
      setCard={setCard}
      prefix="custom_param_desc_"
      carouselCards={carouselCards}
      cardIndex={cardIndex}
      field="description"
    />
  );

  const isCarouselCardMediaDisabled = !carouselCardHeight || !carouselCardWidth;

  return (
    <div className="card p-3 m-0 mt-3">
      <Form.Group className="mt-3">
        <div className="d-flex justify-content-between">
          <Form.Label htmlFor={`${idPrefix}title`}>
            Title
            <span className="text-danger"> * </span>
          </Form.Label>
          <TitleAddVariableButton
            value={card.title}
            setCard={setCard}
            inputRef={titleRef}
            prefix="custom_param_title_"
          />
        </div>
        <Form.Control
          id={`${idPrefix}title`}
          name="title"
          value={card.title}
          onChange={(e) =>
            setCard((prev) => ({
              ...prev,
              title: e.target.value.slice(0, 100),
            }))
          }
          maxLength={100}
          ref={titleRef}
          required
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <div className="d-flex justify-content-between">
          <Form.Label htmlFor={`${idPrefix}description`}>
            Description
            <span className="text-danger"> * </span>
          </Form.Label>
          <DescriptionAddVariableButton
            value={card.description}
            setCard={setCard}
            inputRef={descriptionRef}
            prefix="custom_param_desc_"
          />
        </div>
        <Form.Control
          id={`${idPrefix}description`}
          name="description"
          as="textarea"
          rows={4}
          value={card.description}
          onChange={(e) =>
            setCard((prev) => ({
              ...prev,
              description: e.target.value.slice(0, 500),
            }))
          }
          maxLength={500}
          ref={descriptionRef}
          required
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Label>
          Header
          <span className="text-danger"> * </span>
        </Form.Label>
        <div>
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}mediaType-image`}
            label="Image"
            name={`${idPrefix}mediaType`}
            value="IMAGE"
            checked={card.mediaType === "IMAGE" || !card.mediaType}
            onChange={() =>
              setCard((prev) => ({ ...prev, mediaType: "IMAGE" }))
            }
            required
          />
          <Form.Check
            inline
            type="radio"
            id={`${idPrefix}mediaType-video`}
            label="Video"
            name={`${idPrefix}mediaType`}
            value="VIDEO"
            checked={card.mediaType === "VIDEO"}
            onChange={() =>
              setCard((prev) => ({ ...prev, mediaType: "VIDEO" }))
            }
            required
          />
        </div>
      </Form.Group>
      <Row className="mt-3">
        <Form.Group>
          <Form.Label htmlFor={`${idPrefix}mediaFile`}>
            Upload Media
            <span className="text-danger"> * </span>
          </Form.Label>
          <RcsDropMediaAndPreview
            file={file}
            preview={preview}
            onFileChange={onFileChange}
            inputId={`${idPrefix}mediaFile`}
            accept={mediaAccept}
            disabled={isCarouselCardMediaDisabled}
          />
          {isCarouselCardMediaDisabled && (
            <div className="text-warning small mt-1">
              Please select card height and width before uploading media.
            </div>
          )}
          {mediaError && (
            <Alert variant="danger" className="mt-2 mb-0 py-1 px-2">
              {mediaError}
            </Alert>
          )}
        </Form.Group>
      </Row>
      {/* Render thumbnail upload if VIDEO is selected */}
      {card.mediaType === "VIDEO" && (
        <Row className="mt-3">
          <Form.Group>
            <Form.Label htmlFor={`${idPrefix}thumbnailFile`}>
              Upload Thumbnail <span className="text-danger"> * </span>
            </Form.Label>
            <RcsDropMediaAndPreview
              file={thumbnailFile}
              preview={thumbnailPreview}
              onFileChange={onThumbnailFileChange}
              inputId={`${idPrefix}thumbnailFile`}
              accept={thumbnailAccept}
              disabled={isCarouselCardMediaDisabled}
            />
            {isCarouselCardMediaDisabled && (
              <div className="text-warning small mt-1">
                Please select card height and width before uploading thumbnail.
              </div>
            )}
            {thumbnailError && (
              <Alert variant="danger" className="mt-2 mb-0 py-1 px-2">
                {thumbnailError}
              </Alert>
            )}
          </Form.Group>
        </Row>
      )}
      <h5 className="mt-4">Suggestions</h5>
      <RcsSuggestionsField
        suggestion={suggestionInput}
        setSuggestion={setSuggestionInput}
        addSuggestion={addSuggestion}
        idPrefix={idPrefix}
        suggestions={card.suggestions || []}
        onEdit={onEditSuggestion}
        onDelete={onDeleteSuggestion}
        editingIndex={editingSuggestionIndex}
        setEditingIndex={setEditingSuggestionIndex}
      />
    </div>
  );
};

export default RcsCarouselCardForm;
